import { Box } from '@mui/system';
import {
  Tabs, Tab, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import log from 'loglevel';
import { useFormik } from 'formik';
import isEqual from 'lodash.isequal';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  generatePath, useSearchParams,
} from 'react-router-dom';
import { Education } from '../../icons/education';
import { ExperienceIcon } from '../../icons/experience';
import { KeyAttributes } from '../../icons/key-attribute';
import { Mail } from '../../icons/mail';
import { Skills } from '../../icons/skills';
import { SocialLink } from '../../icons/social-link';
import { SocialPerson } from '../../icons/social-person';
import STRING_KEYS from '../../language/keys';
import useNavigate from '../../hooks/useNavigate';
import { getLatestTalentVersion } from '../../api/talentsApi';
import {
  useUpdateMatch,
  useGetMatchPreview,
  useRejectMatch,
} from '../../hooks/useMatch';
import { Loader } from '../Loader';
import ConfirmationDialog from '../../components/Dialogs/ConfirmationDialog';
import { ROUTES, MATCH_TABS_PARAMS } from '../../consts';
import { ConfirmationDialogType, VIEW_MODE } from '../../consts/enums';
import { CONFIG } from '../../config';
import useExitPrompt from '../../hooks/useExitPrompt';
import { useHistoryLogsMatch } from '../../hooks/useHistoryLogsMatch';
import { ErrorsStack } from '../ErrorsStack';
import { ContactDetail } from '../ContactDetailsModal';
import { HistoryMainPanel } from '../HistoryMainPanel';
import { useAuth } from '../../hooks/useAuth';
import { PERMISSIONS } from '../../consts/permissions';
import MatchLeftNavBar from './MatchLeftNavBar';
import MatchBreadcrumbs from './MatchBreadcrumbs';
import MatchHeader from './MatchHeader';
import { TabPanel } from './TabPanel';
import { TalentInfoTab } from './TalentInfoTab';
// import { MatchHistory } from './MatchHistory';
import { style } from './style';
import { MetadataTab } from './MetadataTab';

const TAG = '[MATCH_EDITOR]';
const PREVIEW_WINDOW_NAME = 'preview_window';
const INVALID_TAB = 'invalid';

const TAB_QUERY_PARAM = 'tab';
const SHOW_HISTORY_PARAM = 'showHistory';

type MatchEditorProps = {
  viewMode: ViewMode,
  accountId: string,
  positionId: string,
  matchId?: string | null, // optional. if not exist - a new match
  match: MatchFormattedResponse,
  talent: FormattedTalentResponse,
  onApplyChanges: () => void,
  setLatestVersion?: (version: string) => void,
  isShowNotification?: boolean,
  isLatestUpdate?: boolean,
  onSaveMatch?: () => void,
  latestTalentVersion?: string | null,
  onContactDetailsUpdate?: (contactDetails: ContactDetail) => void,
  saveDraftEnabled: boolean,
  handleDisableSaveDraft: () => void,
}

type MatchForm = {
  status: string,
};

export const MatchEditor = ({
  viewMode,
  accountId,
  positionId,
  matchId = null,
  match,
  talent,
  onApplyChanges,
  setLatestVersion,
  isShowNotification,
  isLatestUpdate,
  onSaveMatch,
  latestTalentVersion = null,
  onContactDetailsUpdate,
  saveDraftEnabled,
  handleDisableSaveDraft,
}: MatchEditorProps):JSX.Element => {
  const { hasPermissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenContactDetailsModal, setIsOpenContactDetailsModal] = useState<boolean>(false);
  const [openPublishDialog, setPublishOpenDialog] = useState(false);

  const previewWindowRef = useRef<Window | null>(null);
  const tabUrlParam = searchParams.get(TAB_QUERY_PARAM) || MATCH_TABS_PARAMS.TALENT_INFO;
  let currentTab = '';

  switch (tabUrlParam) {
    case MATCH_TABS_PARAMS.TALENT_INFO:
      currentTab = MATCH_TABS_PARAMS.TALENT_INFO;
      break;
    case MATCH_TABS_PARAMS.METADATA:
      currentTab = MATCH_TABS_PARAMS.METADATA;
      break;
    default:
      currentTab = INVALID_TAB;
  }

  const talentInfoRouteMatch = currentTab === MATCH_TABS_PARAMS.TALENT_INFO;

  const { t: translate } = useTranslation();

  const { goToPositionDetails } = useNavigate();

  const {
    data: dataHistory,
    isError: isErrorHistory,
    error: matchHistoryError,
    isFetching: isFetchingHistory,
    refetch,
  } = useHistoryLogsMatch(
    accountId, positionId, matchId || '',
    () => {
      log.debug(TAG, 'loaded match history data');
    });

  const {
    refetch: fetchMatchPreview,
    isLoading: isLoadingMatchPreview,
    error: matchPreviewError,
  } = useGetMatchPreview(accountId, positionId, matchId, false, (response) => {
    log.debug(TAG, 'loaded match-preview successfully', response.match.id);

    if (!previewWindowRef.current || previewWindowRef?.current?.closed) {
      log.debug(TAG, "match preview window doesn't exist - open new window");
      previewWindowRef.current = window.open(
        generatePath(ROUTES.MATCH_PREVIEW, {
          accountId,
          positionId,
          matchId: response.match.id, // todo: change to match-id?
        }),
        PREVIEW_WINDOW_NAME,
      );
    } else {
      log.debug(TAG, 'match preview window already exists');

      previewWindowRef.current.focus();
    }
  });

  const updateMatch = useUpdateMatch(isLatestUpdate || false, () => {
    handleDisableSaveDraft();
    if (typeof onSaveMatch === 'function') {
      onSaveMatch();
    }
  });

  const { mutate: doUpdateMatch } = updateMatch;

  const rejectMatch = useRejectMatch(() => goToPositionDetails(positionId));

  const isLoading = updateMatch.isLoading || rejectMatch.isLoading;

  const checkLatestTalentVersion = () => {
    return  getLatestTalentVersion(talent.talentId)
      .then((talentData) => {
        if (setLatestVersion) {
          setLatestVersion(talentData.version);
        }
      })
      .catch((error) => {
        log.debug(TAG, 'failed to load latest talent version', error);
      });
  };

  useEffect(() => {
    checkLatestTalentVersion();
    const intervalId: NodeJS.Timeout = setInterval(
      checkLatestTalentVersion,
      CONFIG.MATCH_CHECK_TALENT_VERSION_INTERVAL,
    );

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (matchId) {
      // if this isn't a new match

      if (!searchParams.get(TAB_QUERY_PARAM)) {
        const showHistoryParam = searchParams.get(SHOW_HISTORY_PARAM) || '';

        setSearchParams({
          // ...{ [TAB_QUERY_PARAM]: MATCH_TABS_PARAMS.TALENT_INFO },
          ...(showHistoryParam) && { [SHOW_HISTORY_PARAM]: showHistoryParam },
        });
      }
    }
  }, [matchId, searchParams, setSearchParams]);

  // todo: addNewCard(),
  const formik = useFormik<MatchForm>({
    initialValues: {
      status: match.status,
    },
    // validationSchema, // todo: add validation
    onSubmit: async (values): Promise<void> => {
      const saveObject = {
        status: values.status,
      };

      if (!matchId) {
        log.error(TAG, 'missing matchId. cannot save match');
      } else {
        updateMatch.mutate({
          accountId, positionId, matchId, saveObject,
        });
      }
    },
  });

  const {
    id,
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    status,
  } = match;

  const {
    setFieldValue,
    submitForm,
  } = formik;

  const handleSaveDraft = useCallback(() => {
    submitForm();
  }, [submitForm]);

  const isHistoryVisible =
    searchParams.get('showHistory') === 'true' &&
    hasPermissions([PERMISSIONS.MATCH_HISTORY_LOG_VIEW]);

  const handleOpenHistoryView = () => {
    setSearchParams({ showHistory: 'true' });
    refetch();
  };

  const handleCloseHistoryView = () => {
    setSearchParams({ showHistory: 'false' });
  };

  const shouldLeftNavBarRender = (): boolean => {
    return talentInfoRouteMatch;
  };

  const hasChangesInValues = () => {
    const originalValues = { ...match.matchReasons, status: match.status };

    return !isEqual(formik.values, originalValues);
  };

  const handleChangeToDraftClick = useCallback(() => {
    if (!matchId) {
      // cannot change to draft a new match (which doesn't have id yet)
      log.debug(TAG, 'tried to change to draft a match without id!');

      return;
    }
    doUpdateMatch({
      accountId,
      positionId,
      matchId,
      saveObject: {
        status: 'DRAFT', // todo: from consts
      },
    });

    // todo: handle errors from server
  }, [accountId, matchId, positionId, doUpdateMatch]);

  const handlePublishMatchClicked = useCallback(() => {
    setPublishOpenDialog(true);
  }, []);

  const handleClosePublishDialog = useCallback(() => {
    setPublishOpenDialog(false);
  }, []);

  const handlePublishClick = useCallback(() => {
    // todo: strings from consts
    setFieldValue('status', 'PUBLISHED', true);
    submitForm();
  }, [setFieldValue, submitForm]);

  const handleConfirmPublishDialog = useCallback(() => {
    handleClosePublishDialog();
    handlePublishClick();
  }, [handleClosePublishDialog, handlePublishClick]);

  const {
    emails = [],
    phoneNumbers = [],
    socialLinks = [],
    employmentHistory = [],
    educationHistory = [],
    attributes = [],
    technicalSkills = [],
  } = talent || {};

  const toogleContactDetailsModal = (val:boolean) => {
    setIsOpenContactDetailsModal(val);
  };

  const handlePreviewMatchClicked = useCallback(() => {
    fetchMatchPreview();
  }, [fetchMatchPreview]);

  // check changes & prevent page leaving
  const hasChanges = hasChangesInValues();
  useExitPrompt(hasChanges && viewMode === VIEW_MODE.EDITABLE);

  const getLeftNavBarList = () => {
    if (talentInfoRouteMatch) {
      return [
        {
          icon: <SocialPerson />,
          counter: 5,
          hash: '#basic-details',
          title: translate(STRING_KEYS.BASIC_DETAILS),
          isError: false,
        },
        {
          icon: <Mail />,
          counter: emails.length + phoneNumbers.length,
          hash: '#contact-details',
          title: translate(STRING_KEYS.CONTACT_INFORMATION),
          isError: false,
        },
        {
          icon: <SocialLink />,
          counter: socialLinks.length,
          hash: '#social-links',
          title: translate(STRING_KEYS.SOCIAL_LINKS),
          isError: false,
        },
        {
          icon: <ExperienceIcon />,
          counter: employmentHistory.length,
          hash: '#experience',
          title: translate(STRING_KEYS.EXPERIENCE),
          isError: false,
        },
        {
          icon: <Education />,
          counter: educationHistory.length,
          hash: '#education',
          title: translate(STRING_KEYS.EDUCATION),
          isError: false,
        },
        {
          icon: <KeyAttributes />,
          counter: attributes.length,
          hash: '#key-attributes',
          title: translate(STRING_KEYS.KEY_ATTRIBUTES),
          isError: false,
        },
        {
          icon: <Skills />,
          counter: technicalSkills.length,
          hash: '#skills',
          title: translate(STRING_KEYS.SKILLS),
          isError: false,
        },
      ];
    }

    return [];
  };

  if (isLoading) {
    return <Loader top="45%" left="45%" />;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderError = (reactQueryError: any, actionName: string) => {
    return (
      <ErrorsStack
        errors={ reactQueryError?.response?.data?.fieldsErrors || [] }
        errorMessage={
          reactQueryError?.response?.data?.message ||
          translate(STRING_KEYS.FAILED_TO_ACTION, { actionName })
        }
      />
    );
  };

  return (
    <>
      {shouldLeftNavBarRender() && (
        <MatchLeftNavBar
          navList={ getLeftNavBarList() }
        />
      )}
      <Box sx={ style.containerMain.sx }>
        <Box style={ style.containerTalentEdit }>
          <Box style={ style.containerTalentEditInner }>
            <MatchBreadcrumbs
              positionId={ positionId }
            />

            <MatchHeader
              viewMode={ viewMode }
              matchId={ id }
              positionId={ positionId }
              accountId={ accountId }
              updatedAt={ updatedAt || '' }
              updatedBy={ updatedBy || '' }
              createdAt={ createdAt || '' }
              createdBy={ createdBy || '' }
              imageUrl={ talent?.profileImage || '' }
              firstName={ talent?.firstName || '' }
              lastName={ talent?.lastName || '' }
              status={ status }
              onViewHistoryClick={ handleOpenHistoryView }
              onChangeToDraftClick={ handleChangeToDraftClick }
              onSaveDraftClick={ handleSaveDraft }
              onPublishClick={ handlePublishMatchClicked }
              onPreviewMatchClick={ handlePreviewMatchClicked }
              isLoadingPreview={ isLoadingMatchPreview }
              saveDraftEnabled={ saveDraftEnabled }
              isLocked={ match.isLocked }
            />

            <ConfirmationDialog
              isOpen={ openPublishDialog }
              title={ translate(STRING_KEYS.MATCH_PUBLISH_DIALOG.TITLE) }
              subTitle={ translate(STRING_KEYS.MATCH_PUBLISH_DIALOG.SUB_TITLE) }
              btnTitle={ translate(STRING_KEYS.MATCH_PUBLISH_DIALOG.ACTION_BTN) }
              onConfirm={ handleConfirmPublishDialog }
              onClose={ handleClosePublishDialog }
              type={ ConfirmationDialogType.primary }
            />

            {matchPreviewError && (
              renderError(matchPreviewError, translate(STRING_KEYS.LOAD_MATCH_PREVIEW_ACTION))
            )}

            {updateMatch.isError && (
              renderError(updateMatch.error, translate(STRING_KEYS.UPDATE_MATCH_ACTION))
            )}

            {rejectMatch.isError && (
              renderError(rejectMatch.error, translate(STRING_KEYS.REJECT_MATCH_ACTION))
            )}

            {matchId && (
              <>
                <Box sx={ style.tabsContainer.sx }>
                  <Tabs value={ currentTab }>
                    <Tab
                      label={ translate(STRING_KEYS.MATCH_PAGE_TAB_TALENT) }
                      value={ MATCH_TABS_PARAMS.TALENT_INFO }
                      onClick={ () => {
                        setSearchParams({ [TAB_QUERY_PARAM]: MATCH_TABS_PARAMS.TALENT_INFO });
                      } }
                    />
                    <Tab
                      label={ translate(STRING_KEYS.MATCH_PAGE_TAB_METADATA) }
                      value={ MATCH_TABS_PARAMS.METADATA }
                      onClick={ () => {
                        setSearchParams({ [TAB_QUERY_PARAM]: MATCH_TABS_PARAMS.METADATA });
                      } }
                    />
                  </Tabs>
                  <Divider />
                </Box>
                <TabPanel isTabVisible={ currentTab === MATCH_TABS_PARAMS.TALENT_INFO }>
                  <TalentInfoTab
                    viewMode={ viewMode }
                    talent={ talent }
                    isShowNotification={ isShowNotification }
                    latestTalentVersion={ latestTalentVersion }
                    onApplyChanges={ onApplyChanges }
                    isOpenModal={ isOpenContactDetailsModal }
                    onUpdateClick={ toogleContactDetailsModal }
                    onContactDetailsUpdate={ onContactDetailsUpdate }
                  />
                </TabPanel>
                <TabPanel isTabVisible={ currentTab === MATCH_TABS_PARAMS.METADATA }>
                  <MetadataTab
                    match={ match }
                  />
                </TabPanel>
                <TabPanel isTabVisible={ currentTab === INVALID_TAB }>
                  {/* TO DO design invalid tab */}
                  <div>
                    invalid tab
                  </div>
                </TabPanel>

              </>
            )}
          </Box>
        </Box>
        <HistoryMainPanel
          isError={ isErrorHistory }
          error={ matchHistoryError }
          isOpened={ isHistoryVisible }
          onCloseClick={ handleCloseHistoryView }
          data={ dataHistory }
          isFetching={ isFetchingHistory }
          entity={ translate(STRING_KEYS.MATCH) }
        />
      </Box>
    </>
  );
};

MatchEditor.defaultProps = {
  matchId: null,
  setLatestVersion: null,
  isShowNotification: false,
  isLatestUpdate: false,
  onSaveMatch: null,
  latestTalentVersion: null,
  onContactDetailsUpdate: undefined,
};
